import { ApiError } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { serviceRolesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { warnIfTruncated } from '@/lib/warn-if-truncated';
import { ServiceRoleAutocomplete, serviceRolePreloadQuery } from './service-role-autocomplete';

// Optional, non-unique, free-form label. Empty string is allowed and means
// "no code"; the API normalizes it to null.
const codeField = z.string().max(64, 'Must be 64 characters or fewer.');

export const ProductFormSchema = z.object({
  retainerCode: codeField,
  tmCode: codeField,
  name: z.string().min(1, 'Required').max(200),
  description: z.string().max(4000).optional().or(z.literal('')),
  serviceRoleId: z.string().uuid('Pick a primary service role'),
  // `.optional()` so the form-level type matches the API client's type
  // exactly (`string[] | undefined`). The chip-list handles undefined as
  // empty. blankProductValues initializes it to `[]`.
  visibleToServiceRoleIds: z
    .array(z.string().uuid())
    .max(50, 'Too many additional roles')
    .optional(),
  internal: z.boolean(),
  excludeFromBudget: z.boolean(),
});

export type ProductFormValues = z.infer<typeof ProductFormSchema>;

export const PRODUCT_FORM_FIELD_NAMES = [
  'retainerCode',
  'tmCode',
  'name',
  'description',
  'serviceRoleId',
  'visibleToServiceRoleIds',
  'internal',
  'excludeFromBudget',
] as const satisfies ReadonlyArray<keyof ProductFormValues>;

export function isProductFormField(field: string): field is keyof ProductFormValues {
  return (PRODUCT_FORM_FIELD_NAMES as readonly string[]).includes(field);
}

export const blankProductValues: ProductFormValues = {
  retainerCode: '',
  tmCode: '',
  name: '',
  description: '',
  serviceRoleId: '',
  visibleToServiceRoleIds: [],
  internal: false,
  excludeFromBudget: false,
};

interface ProductFormFieldsProps {
  form: UseFormReturn<ProductFormValues>;
  /** Hide the Internal/Customer "Kind" toggle. Used by the customer-scoped
   *  activity form, where a custom activity's `internal` is always its
   *  customer's and is set programmatically rather than chosen. */
  hideInternalToggle?: boolean;
}

// Same query the ServiceRoleAutocomplete preloads with, so the catalog this
// form holds (for chip labels and "Select all") is literally the same cache
// entry the dropdowns read — one fetch, and no way for the two to disagree
// about which roles exist.
const SERVICE_ROLES_LIST_QUERY = serviceRolePreloadQuery({ active: true });

export function ProductFormFields({ form, hideInternalToggle }: ProductFormFieldsProps) {
  const errors = form.formState.errors;
  const serviceRoles = useQuery({
    queryKey: queryKeys.serviceRoles.list(SERVICE_ROLES_LIST_QUERY),
    queryFn: () => serviceRolesApi.list(SERVICE_ROLES_LIST_QUERY),
  });
  warnIfTruncated(serviceRoles.data, 'product-form service-role select');
  const roles = serviceRoles.data?.data ?? [];

  return (
    <div className="space-y-4">
      <Field
        id="name"
        label="Name"
        placeholder="Fractional CFO"
        error={errors.name?.message}
        register={form.register('name')}
      />
      <FieldTextarea
        id="description"
        label="Description"
        placeholder="Enter activity description"
        error={errors.description?.message}
        register={form.register('description')}
      />
      <PrimaryRoleField
        value={form.watch('serviceRoleId')}
        onChange={(v) => {
          form.setValue('serviceRoleId', v, { shouldValidate: true, shouldDirty: true });
          // If the new primary was previously listed as an extra, drop it
          // from extras to keep the implicit-visibility invariant.
          const extras = form.getValues('visibleToServiceRoleIds') ?? [];
          if (extras.includes(v)) {
            form.setValue(
              'visibleToServiceRoleIds',
              extras.filter((x) => x !== v),
              { shouldDirty: true },
            );
          }
        }}
        error={errors.serviceRoleId?.message}
      />
      <ExtraRolesField
        value={form.watch('visibleToServiceRoleIds') ?? []}
        primary={form.watch('serviceRoleId')}
        roles={roles}
        loading={serviceRoles.isLoading}
        onChange={(v) =>
          form.setValue('visibleToServiceRoleIds', v, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={errors.visibleToServiceRoleIds?.message}
      />
      {hideInternalToggle ? null : (
        <div className="space-y-1.5">
          <Label htmlFor="kind">Kind</Label>
          <Select
            // See PrimaryRoleField: remount on the hydrated value so an Internal
            // activity edited from a cached form doesn't stay stuck on "Customer".
            key={form.watch('internal') ? 'internal' : 'customer'}
            value={form.watch('internal') ? 'internal' : 'customer'}
            onValueChange={(v) =>
              form.setValue('internal', v === 'internal', { shouldDirty: true })
            }
          >
            <SelectTrigger id="kind" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">Customer</SelectItem>
              <SelectItem value="internal">Internal</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Internal activities are not billed to customers — used for back-office or admin time.
          </p>
        </div>
      )}
      <div className="surface-card flex items-start gap-2 rounded-md border p-3">
        <Checkbox
          id="excludeFromBudget"
          checked={form.watch('excludeFromBudget')}
          onCheckedChange={(c) =>
            form.setValue('excludeFromBudget', c === true, { shouldDirty: true })
          }
          className="mt-0.5"
        />
        <div className="space-y-0.5">
          <Label htmlFor="excludeFromBudget">Exclude from budget consumption</Label>
          <p className="text-xs text-muted-foreground">
            Entries logged under this activity never count toward a customer's budget, and can never
            push it over. Use this for catch-all/unplanned work like "Adhoc Work."
          </p>
        </div>
      </div>
      <Field
        id="retainerCode"
        label="Retainer code"
        placeholder="CFO_FRACTIONAL_RET"
        hint="Billing code used when this activity is part of a retainer."
        error={errors.retainerCode?.message}
        register={form.register('retainerCode')}
      />
      <Field
        id="tmCode"
        label="T&M code"
        placeholder="CFO_FRACTIONAL_TM"
        hint="Billing code used for time-&-materials work."
        error={errors.tmCode?.message}
        register={form.register('tmCode')}
      />
    </div>
  );
}

interface FieldProps {
  id: keyof ProductFormValues;
  label: string;
  type?: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  register: ReturnType<UseFormReturn<ProductFormValues>['register']>;
}

function Field({ id, label, type, placeholder, hint, error, disabled, register }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type ?? 'text'}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        disabled={disabled}
        {...register}
      />
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function FieldTextarea({ id, label, placeholder, error, register }: Omit<FieldProps, 'type'>) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        rows={3}
        {...register}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * PATCH semantics: `undefined` = don't change. Collapses "" → undefined so
 * untouched/cleared fields are treated as "leave alone" rather than
 * "set to null".
 */
export function blankToUndef(v: string | undefined): string | undefined {
  return v === '' || v === undefined ? undefined : v;
}

/**
 * Build the create/patch body shared by both activity forms (the global
 * `ProductFormSheet` and the customer-scoped `CustomerActivityFormSheet`).
 * `customerId` is NOT part of this — it's carried in the route, not the body.
 */
export function productPayloadFromValues(values: ProductFormValues) {
  return {
    retainerCode: values.retainerCode,
    tmCode: values.tmCode,
    name: values.name,
    serviceRoleId: values.serviceRoleId,
    visibleToServiceRoleIds: values.visibleToServiceRoleIds,
    internal: values.internal,
    excludeFromBudget: values.excludeFromBudget,
    description: blankToUndef(values.description),
  };
}

/**
 * Shared mutation error handler for both activity forms: map a server
 * `ValidationError`'s field errors onto the form inputs (G12 — errors shown in
 * context), or toast a generic message for everything else. Keeping this in one
 * place stops the two sheets from drifting if the schema or error shape changes.
 */
export function handleProductMutationError(
  form: UseFormReturn<ProductFormValues>,
  err: unknown,
): void {
  if (err instanceof ApiError && err.fields?.length) {
    for (const fe of err.fields) {
      if (isProductFormField(fe.path)) form.setError(fe.path, { message: fe.message });
    }
    toast.error('Please fix the highlighted fields');
    return;
  }
  toast.error(err instanceof Error ? err.message : 'Save failed');
}

interface RoleOption {
  id: string;
  name: string;
  shortCode: string;
}

/**
 * Required, single picker for the primary service role. Uses the shared
 * `ServiceRoleAutocomplete` (the same searchable dropdown Budgets and the
 * customer-team sheet use) rather than a raw Select — with a full role
 * catalog, scrolling an unsearchable list was the slow way to find one role.
 * It also resolves the selected role by id, so the edit-mode "value applied
 * after mount" problem the old Select needed a `key` remount for is gone.
 */
function PrimaryRoleField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="serviceRoleId">Service role (primary)</Label>
      <ServiceRoleAutocomplete
        id="serviceRoleId"
        active
        value={value || null}
        onChange={(r) => onChange(r?.id ?? '')}
        placeholder="Select…"
        hasError={Boolean(error)}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * Multi-select for extra "also visible to" roles. The primary role is
 * excluded — adding it here would be redundant (it's implicitly visible).
 * Mirrors the chip + add-via-Select pattern from `custom-fields-editor`.
 */
function ExtraRolesField({
  value,
  primary,
  roles,
  loading,
  onChange,
  error,
}: {
  value: string[];
  primary: string;
  roles: readonly RoleOption[];
  loading: boolean;
  onChange: (next: string[]) => void;
  error?: string;
}) {
  const picked = value ?? [];
  const byId = new Map(roles.map((r) => [r.id, r]));
  const available = roles.filter((r) => r.id !== primary && !picked.includes(r.id));
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor="visibleToServiceRoleIds">Also visible to</Label>
        {!loading && roles.length > 0 ? (
          <div className="flex items-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground"
              disabled={available.length === 0}
              onClick={() => onChange(roles.filter((r) => r.id !== primary).map((r) => r.id))}
            >
              Select all
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground"
              disabled={picked.length === 0}
              onClick={() => onChange([])}
            >
              Clear all
            </Button>
          </div>
        ) : null}
      </div>
      {loading ? (
        <Skeleton className="h-9 w-full" />
      ) : (
        <>
          {picked.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {picked.map((id) => {
                const r = byId.get(id);
                const label = r ? `${r.shortCode} · ${r.name}` : id;
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs"
                  >
                    {label}
                    <button
                      type="button"
                      aria-label={`Remove ${label}`}
                      onClick={() => onChange(picked.filter((x) => x !== id))}
                      className="hover:text-destructive"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Only the primary role can see this activity.
            </p>
          )}
          {available.length > 0 ? (
            // Same picker as the primary field, held at `value={null}` so it
            // acts as an "add one" affordance: each pick appends a chip and
            // the input resets to its placeholder. Already-picked roles and
            // the primary are excluded rather than merely ignored.
            <ServiceRoleAutocomplete
              id="visibleToServiceRoleIds"
              active
              value={null}
              excludeIds={primary ? [primary, ...picked] : picked}
              onChange={(r) => {
                if (r) onChange([...picked, r.id]);
              }}
              placeholder={picked.length === 0 ? 'Add a role…' : 'Add another role…'}
            />
          ) : null}
        </>
      )}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
